/**
 * Consumable labels (2026-10-05): a 4x6 label per item with a cut-out
 * 3" x 3/4" bin insert along the bottom (Uline S-21114 bin label holders).
 *
 * GET /reports/consumable_labels.pdf?ids=71,72&copies=1 renders them; the
 * browser prints the PDF to the 4x6 label printer the same way the material
 * labels do (openPdfInPrintDialog). Print at 100% / Actual Size so the insert
 * comes out exactly 3.00" x 0.75".
 */
import openPdfInPrintDialog from './openPdfInPrintDialog';
import { apiErrorMessage } from './apiError';

export const MAX_LABEL_ITEMS = 200;    // jobq-api CONSUMABLE_LABEL_MAX_IDS
export const MAX_LABEL_COPIES = 25;    // jobq-api CONSUMABLE_LABEL_MAX_COPIES
export const MAX_LABEL_PAGES = 300;    // jobq-api CONSUMABLE_LABEL_MAX_PAGES

export function consumableLabelsUrl(apiBase, ids, copies = 1) {
  const list = [...new Set((ids || []).map(Number).filter((n) => Number.isInteger(n) && n > 0))];
  const params = new URLSearchParams({ ids: list.join(','), copies: String(copies) });
  return `${apiBase}/reports/consumable_labels.pdf?${params.toString()}`;
}

/** Why this request would be refused, or null. Mirrors the API's own limits. */
export function labelRequestProblem(ids, copies) {
  const n = new Set(ids || []).size;
  const c = Number(copies);
  if (!n) return 'Pick at least one item to label.';
  if (n > MAX_LABEL_ITEMS) return `At most ${MAX_LABEL_ITEMS} items per print (${n} picked).`;
  if (!Number.isInteger(c) || c < 1 || c > MAX_LABEL_COPIES) return `Copies must be 1 to ${MAX_LABEL_COPIES}.`;
  if (n * c > MAX_LABEL_PAGES) return `That is ${n * c} labels; at most ${MAX_LABEL_PAGES} per print.`;
  return null;
}

/**
 * Fetch the labels and hand them to the browser: `mode` 'print' opens the
 * print dialog, 'open' shows the PDF in a new tab. Throws Error(message) on a
 * refused request so the caller can show it.
 *
 * For 'open', pass `targetWindow` = window.open('', '_blank') called in the
 * click handler BEFORE anything is awaited: a big batch takes seconds to
 * render, and by then the browser no longer counts the click, so a tab opened
 * afterwards is silently blocked as a popup.
 */
export async function fetchConsumableLabels(apiBase, ids, copies = 1, mode = 'print', targetWindow = null) {
  try {
    const problem = labelRequestProblem(ids, copies);
    if (problem) throw new Error(problem);
    const res = await fetch(consumableLabelsUrl(apiBase, ids, copies));
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Could not make the labels'));
    const blob = await res.blob();
    deliver(blob, mode, targetWindow);
  } catch (err) {
    if (targetWindow && !targetWindow.closed) targetWindow.close();
    throw err;
  }
}

function deliver(blob, mode, targetWindow) {
  if (mode === 'open' && targetWindow && !targetWindow.closed) {
    const url = URL.createObjectURL(blob);
    targetWindow.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } else if (mode === 'open') {
    // No pre-opened tab (blocked, or none passed): same mechanism as utils/openPdf.js.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } else {
    openPdfInPrintDialog(blob);
  }
}
